// frontend/src/charts/echartsOption.js
// Pure: the backend's shaped result ({labels, series}) + a chart type + the
// theme -> an ECharts option. No DOM, no fetching; unit-tested.
//
// Mark specs (dataviz guidance): bars <= 24px with a 4px rounded data end,
// 2px lines, 10% area wash, hairline solid grid, a 2px surface gap between
// stacked segments and donut slices, legend only for 2+ series, every
// text in text ink (never the series colour), one value axis only.
//
// Used by the app (ChartView) AND the embed page (embedEntry.js, bundled
// into the backend's vendored connex-embed file), so both draw identically.
//
// Step 7b-2b: a chart's presentation (format.js, decision D10) decides how
// numbers are written on the axis, in the tooltip and in value labels, and
// whether value labels are drawn (bars, lines, areas). A zero bar has no
// height, so its label is what shows it is there.
// Step 13c-4: under "Automatic" each series follows its measure's own format
// (the result `columns` carry it): tooltips and value labels per series, the
// value axis after the first series (one axis cannot carry two units).
import { MAX_SERIES, FALLBACK_THEME } from './palette';
import { makeFormatter, normalizePresentation, presentationFor, seriesFormats } from './format';

/** Types drawn on the canvas. 'card' and 'table' are rendered by KpiCard / DataTable. */
export const CANVAS_TYPES = [
  'line', 'area', 'bar', 'stackedBar', 'horizontalBar', 'doughnut',
  // Also drawn so existing embeds keep rendering; the builder exposes them later.
  'pie', 'stackedArea', 'scatter', 'polarArea', 'radar',
];

const FONT = "'Geist Variable', system-ui, -apple-system, 'Segoe UI', sans-serif";

/** Chart types that can draw a number on each mark (slices: name, value and share of the total). */
export const VALUE_LABEL_TYPES = ['bar', 'stackedBar', 'horizontalBar', 'line', 'area', 'stackedArea', 'doughnut', 'pie', 'polarArea'];
const SLICE_TYPES = ['doughnut', 'pie', 'polarArea'];
const shareFmt = new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 1 });

function baseOption(theme, showLegend, tooltipFormat) {
  return {
    animationDuration: 300,
    color: theme.series,
    textStyle: { fontFamily: FONT, color: theme.textSecondary },
    aria: { enabled: true },
    legend: {
      show: showLegend, type: 'scroll', top: 0, left: 0, icon: 'roundRect', itemWidth: 10, itemHeight: 10,
      textStyle: { color: theme.textSecondary, fontSize: 12 },
      pageTextStyle: { color: theme.textMuted },
    },
    tooltip: {
      backgroundColor: theme.surface, borderColor: theme.grid, borderWidth: 1,
      textStyle: { color: theme.text, fontSize: 12 },
      valueFormatter: tooltipFormat,
    },
  };
}

function categoryAxis(theme, labels, extra = {}) {
  return {
    type: 'category', data: labels,
    axisLine: { lineStyle: { color: theme.axis } }, axisTick: { show: false },
    // Up to 12 categories: try every label (overlaps are still hidden);
    // more than that, let ECharts thin them out.
    axisLabel: { color: theme.textMuted, hideOverlap: true, interval: labels.length <= 12 ? 0 : 'auto' },
    ...extra,
  };
}

function valueAxis(theme, axisFormat) {
  return {
    type: 'value',
    splitLine: { lineStyle: { color: theme.grid, width: 1, type: 'solid' } },
    axisLabel: { color: theme.textMuted, formatter: axisFormat },
  };
}

/**
 * A number on each mark, in text ink. Empty periods draw no label. Inside a
 * stack a 0 segment has no height, so its label would sit on the next one:
 * it is left out there (a plain zero bar keeps its "0").
 */
function valueLabel(theme, labelFormat, position, onColour) {
  const label = {
    show: true, position, color: theme.text, fontSize: 11,
    formatter: (p) => (typeof p.value === 'number' && Number.isFinite(p.value) && !(onColour && p.value === 0)
      ? labelFormat(p.value) : ''),
  };
  // Inside a stacked segment the text sits on the series colour: a surface halo keeps it readable.
  if (onColour) Object.assign(label, { textBorderColor: theme.surface, textBorderWidth: 2 });
  return label;
}

/**
 * Which points need a dot to be seen. A line needs two points; a point with
 * no drawn neighbour would otherwise be invisible. With 'connect' the line
 * skips empty periods, so only a series with a single real point needs one.
 */
export function loneDots(values, connect) {
  const v = values || [];
  const real = v.filter((x) => x != null).length;
  return v.map((x, i) => x != null && (connect
    ? real === 1
    : (i === 0 || v[i - 1] == null) && (i === v.length - 1 || v[i + 1] == null)));
}

function cartesian(type, labels, series, theme, legend, emptyPeriods, pres, formats) {
  const horizontal = type === 'horizontalBar';
  const stacked = type === 'stackedBar';
  const isBar = type === 'bar' || stacked || horizontal;
  const isScatter = type === 'scatter';
  const isArea = type === 'area' || type === 'stackedArea';
  const labelled = pres.value_labels && VALUE_LABEL_TYPES.includes(type) && !SLICE_TYPES.includes(type);

  const out = series.map((s, i) => {
    const item = { name: s.name, type: isBar ? 'bar' : (isScatter ? 'scatter' : 'line'), data: s.values, emphasis: { focus: 'series' } };
    // This series' numbers: the measure's own format under "auto" (13c-4), else the chart's.
    const own = formats[i] ? presentationFor(pres, formats[i]) : pres;
    const labelFormat = makeFormatter(own, 'label');
    if (formats[i]) item.tooltip = { valueFormatter: makeFormatter(own, 'tooltip') };
    if (isScatter) {
      item.symbolSize = 10;
      item.itemStyle = { borderColor: theme.surface, borderWidth: 2 };      // surface ring on markers
    } else if (isBar) {
      item.barMaxWidth = 24;
      if (stacked) {
        item.stack = 'total';
        item.itemStyle = { borderColor: theme.surface, borderWidth: 1 };   // 2px gap between segments
      } else {
        item.itemStyle = { borderRadius: horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0] };
      }
    } else {
      item.lineStyle = { width: 2, cap: 'round', join: 'round' };
      item.symbol = 'circle';
      item.symbolSize = 8;
      // empty_periods comes from the server (zero | gap | connect); zero has
      // already been filled in, so only 'connect' changes how nulls are drawn.
      const connect = emptyPeriods === 'connect';
      item.connectNulls = connect;
      const dots = loneDots(s.values, connect);
      item.showSymbol = dots.some(Boolean);
      if (item.showSymbol) item.symbolSize = (_value, params) => (dots[params.dataIndex] ? 8 : 0);
      item.itemStyle = { borderColor: theme.surface, borderWidth: 2 };      // surface ring on markers
      if (isArea) item.areaStyle = { opacity: 0.1 };
      if (type === 'stackedArea') item.stack = 'total';
      // A label sits on its point's symbol, so every point keeps one (size 0 unless it is a lone dot).
      if (labelled && !item.showSymbol) { item.showSymbol = true; item.symbolSize = 0; }
    }
    if (labelled) {
      item.label = valueLabel(theme, labelFormat, stacked ? 'inside' : (horizontal ? 'right' : 'top'), stacked);
      item.labelLayout = { hideOverlap: true };
    }
    return item;
  });

  const axisFormat = makeFormatter(formats[0] ? presentationFor(pres, formats[0]) : pres, 'axis');
  const cat = categoryAxis(theme, labels, horizontal ? { inverse: true } : {});
  // Room for labels above the tallest bar or point, or right of the longest horizontal bar.
  const top = (legend ? 36 : 12) + (labelled && !horizontal ? 12 : 0);
  return {
    grid: { left: 8, right: labelled && horizontal ? 56 : 16, top, bottom: 8 },
    xAxis: horizontal ? valueAxis(theme, axisFormat) : cat,
    yAxis: horizontal ? cat : valueAxis(theme, axisFormat),
    tooltip: { trigger: 'axis', axisPointer: { type: isBar ? 'shadow' : 'line' } },
    series: out,
  };
}

/**
 * doughnut (hollow), pie (full) and polarArea (rose: slice radius follows the value).
 * With value labels (7b-2d, Power BI's "detail labels"): each slice is named
 * outside the ring on a leader line, with its value and share of the total;
 * the ring shrinks to leave room, and labels that would collide are hidden.
 */
function circular(type, labels, series, theme, pres) {
  const first = series[0] || { name: '', values: [] };
  const labelled = pres.value_labels;
  const item = {
    name: first.name, type: 'pie', center: ['50%', '56%'],
    radius: type === 'doughnut' ? (labelled ? ['40%', '60%'] : ['55%', '80%']) : ['0%', labelled ? '60%' : '78%'],
    avoidLabelOverlap: true, label: { show: false },
    itemStyle: { borderColor: theme.surface, borderWidth: 2, borderRadius: 4 },
    // A slice of 0 or no value draws nothing; leave it out of the legend too.
    data: (labels || []).map((name, i) => ({ name, value: first.values[i] }))
      .filter((d) => d.value != null && d.value !== 0),
  };
  if (labelled) {
    const valueText = makeFormatter(pres, 'label');
    item.label = {
      show: true, position: 'outside', color: theme.text, fontSize: 11, lineHeight: 15,
      width: 130, overflow: 'truncate',
      // Canvas text, never HTML; the name is the category label from the data.
      formatter: (p) => `${p.name}\n${valueText(p.value)} · ${shareFmt.format((Number(p.percent) || 0) / 100)}`,
    };
    item.labelLine = { show: true, length: 10, length2: 10, lineStyle: { color: theme.axis, width: 1 } };
    item.labelLayout = { hideOverlap: true };
  }
  if (type === 'polarArea') item.roseType = 'area';
  return { tooltip: { trigger: 'item' }, legend: { show: true }, series: [item] };
}

function radar(labels, series, theme, legend) {
  return {
    tooltip: { trigger: 'item' },
    radar: {
      indicator: (labels || []).map((name) => ({ name })),
      center: ['50%', legend ? '58%' : '52%'], radius: '62%',
      axisName: { color: theme.textMuted },
      splitLine: { lineStyle: { color: theme.grid } },
      splitArea: { show: false },
      axisLine: { lineStyle: { color: theme.axis } },
    },
    series: [{
      type: 'radar', symbol: 'circle', symbolSize: 8,
      lineStyle: { width: 2 }, areaStyle: { opacity: 0.1 },
      itemStyle: { borderColor: theme.surface, borderWidth: 2 },
      data: series.map((s) => ({ name: s.name, value: s.values })),
    }],
  };
}

function merge(base, extra) {
  return {
    ...base, ...extra,
    legend: { ...base.legend, ...(extra.legend || {}) },
    tooltip: { ...base.tooltip, ...(extra.tooltip || {}) },
  };
}

/**
 * Returns { option, hiddenSeries }. hiddenSeries > 0 when the result has more
 * than 8 series; the caller says so instead of inventing a 9th colour.
 */
export function buildOption({ chart, type, theme = FALLBACK_THEME, presentation = null, columns = null }) {
  if (!CANVAS_TYPES.includes(type)) throw new Error(`Not a canvas chart type: ${type}`);
  const pres = normalizePresentation(presentation);
  const labels = (chart && chart.labels) || [];
  const all = (chart && chart.series) || [];
  const series = all.slice(0, MAX_SERIES);
  const hiddenSeries = all.length - series.length;
  // The measures' own formats, one per series (13c-4); all null without columns.
  const formats = seriesFormats(columns, series.length);
  const first = formats[0] ? presentationFor(pres, formats[0]) : pres;
  const tooltipFormat = makeFormatter(first, 'tooltip');

  if (type === 'doughnut' || type === 'pie' || type === 'polarArea') {
    // Slices are one measure, the first series: its format throughout.
    return { option: merge(baseOption(theme, true, tooltipFormat), circular(type, labels, series, theme, first)), hiddenSeries };
  }
  const legend = series.length >= 2;
  if (type === 'radar') {
    return { option: merge(baseOption(theme, legend, tooltipFormat), radar(labels, series, theme, legend)), hiddenSeries };
  }
  const emptyPeriods = chart && chart.empty_periods;
  return {
    option: merge(baseOption(theme, legend, tooltipFormat), cartesian(type, labels, series, theme, legend, emptyPeriods, pres, formats)),
    hiddenSeries,
  };
}
